import React, { useCallback, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faMagnifyingGlass,
  faMinus,
  faUser,
  faUserGroup,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import { APISD } from "../../../../../../../../config/apisd";
import { APILA } from "../../../../../../../../config/apila";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../NotificationToast/ToastSuccess";
import "bulma/css/bulma.css";
import "../../../../../../../../index.css";
import "./StudentModal.css";

export default function ModalConfigStudent({
  show,
  onHide,
  row,
  gradeLevelId = "",
  form2 = {},
  semesterOptions = [],
  fetchParams,
  outputIdUser = "",
  studentMap = {},
  setStudentMap,
  getStudentKey,
  GetResponseData,
}) {
  const [classGroups, setClassGroups] = useState([]);
  const [selectedByClass, setSelectedByClass] = useState({});
  const [expandedClass, setExpandedClass] = useState({});
  const [studentSearch, setStudentSearch] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [applyingStudents, setApplyingStudents] = useState(false);
  const [selectedSemesters, setSelectedSemesters] = useState([]);

  const safeString = (value) =>
    value === null || value === undefined ? "" : String(value);

  const normalizeId = (value) => safeString(value).trim();

  const toTitleCase = (value) => {
    const text = safeString(value).toLowerCase().trim();
    if (!text) return "";

    return text.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
  };

  const getStudentId = (student) =>
    normalizeId(
      student?.ysb_student_id ||
        student?.student_id ||
        student?.id ||
        student?.nisn ||
        ""
    );

  const getStudentName = (student) =>
    student?.nama_lengkap ||
    student?.nama_siswa ||
    student?.student_name ||
    student?.name ||
    student?.full_name ||
    "Siswa";

  const getStudentInitials = (student) => {
    const name = getStudentName(student)
      .replace(/[^a-zA-ZÀ-ÿ\s]/g, " ")
      .trim();

    if (!name) return "S";

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  };

  const getSemesterNumberFromOption = (semesterOption) =>
    semesterOption?.raw?.semester ||
    semesterOption?.raw?.name_semester ||
    semesterOption?.label?.replace("Semester ", "") ||
    "";

  const semesterChoices = useMemo(() => {
    // PRIORITAS: ambil semester langsung dari response student-config
    // yang dibawa lewat `row`. Field yang dipakai sesuai response API:
    // ysb_semester_id + name_semester.
    const targetLevel = (row?.grade_levels || []).find(
      (level) =>
        String(level?.ysb_grade_level_id) === String(gradeLevelId)
    );

    const semesterMap = new Map();

    (targetLevel?.kelas || []).forEach((kelas) => {
      (kelas?.semesters || []).forEach((semesterItem) => {
        const semesterId =
          semesterItem?.ysb_semester_id || semesterItem?.id || "";
        const semesterName =
          semesterItem?.name_semester ??
          semesterItem?.semester ??
          semesterItem?.label ??
          "";
        const semesterNumber = Number(semesterName);

        if (!semesterId || !semesterName || !Number.isFinite(semesterNumber) || semesterNumber <= 0) return;

        if (!semesterMap.has(String(semesterId))) {
          semesterMap.set(String(semesterId), {
            id: semesterId,
            number: semesterNumber,
            label: `Semester ${semesterNumber}`,
            raw: semesterItem,
          });
        }
      });
    });

    const semesterFromStudentConfig = Array.from(semesterMap.values()).sort(
      (a, b) => a.number - b.number
    );

    if (semesterFromStudentConfig.length > 0) {
      return semesterFromStudentConfig;
    }

    // Fallback lama, dipakai hanya kalau response student-config
    // memang tidak membawa data semester.
    return (semesterOptions || [])
      .map((item) => ({
        id: item?.id || "",
        number: Number(getSemesterNumberFromOption(item)),
        label:
          item?.label ||
          `Semester ${getSemesterNumberFromOption(item) || ""}`.trim(),
        raw: item?.raw || null,
      }))
      .filter((item) => item.id && Number.isFinite(item.number))
      .sort((a, b) => a.number - b.number);
  }, [row, gradeLevelId, semesterOptions]);

  const targetSemesterNumber = Number(
    row?.targetSemester || row?.targetSemesterName || semesterChoices?.[0]?.number || 1
  );

  const targetSemesterOption = useMemo(
    () =>
      semesterChoices.find(
        (item) =>
          row?.targetSemesterId &&
          String(item.id) === String(row.targetSemesterId)
      ) ||
      semesterChoices.find(
        (item) => String(item.number) === String(targetSemesterNumber)
      ) ||
      semesterChoices?.[0] ||
      null,
    [semesterChoices, targetSemesterNumber, row?.targetSemesterId]
  );

  const sourceSemesterNumber = targetSemesterOption?.number || 1;
  const sourceSemesterId = targetSemesterOption?.id || "";
  const isUpdateMode = row?.studentMode === "update";

  const getSemesterChoiceByNumber = useCallback(
    (semesterNumber) =>
      semesterChoices.find(
        (item) => String(item.number) === String(semesterNumber)
      ) || null,
    [semesterChoices]
  );

  const getSemesterDataByNumber = useCallback(
    (kelas, semesterNumber) => {
      const semesters = Array.isArray(kelas?.semesters) ? kelas.semesters : [];
      const semesterChoice = getSemesterChoiceByNumber(semesterNumber);
      const semesterId = semesterChoice?.id || "";

      return semesters.find((item) => {
        const itemSemesterNumber =
          item?.semester || item?.name_semester || item?.label || "";
        const itemSemesterId = item?.ysb_semester_id || item?.id || "";

        return (
          String(itemSemesterNumber) === String(semesterNumber) ||
          (semesterId && String(itemSemesterId) === String(semesterId))
        );
      });
    },
    [getSemesterChoiceByNumber]
  );

  const getSemesterData = useCallback(
    (kelas) => getSemesterDataByNumber(kelas, sourceSemesterNumber),
    [getSemesterDataByNumber, sourceSemesterNumber]
  );

  const availableClasses = useMemo(() => {
    if (!row) return [];

    const targetLevel = (row?.grade_levels || []).find(
      (level) =>
        String(level?.ysb_grade_level_id) === String(gradeLevelId)
    );

    return (targetLevel?.kelas || [])
      .filter((kelas) => String(getSemesterData(kelas)?.state_form) === "1")
      .map((kelas) => {
        const kelasId = kelas?.ysb_kelas_id || "";
        const nameLevel = targetLevel?.name_level || "";
        const nameKelas = kelas?.name_kelas || kelasId;
        const rawTitle = [nameLevel, nameKelas].filter(Boolean).join(" ");
        const title = String(rawTitle).toLowerCase().startsWith("kelas")
          ? rawTitle
          : `Kelas ${toTitleCase(rawTitle)}`;

        return {
          kelasId,
          title,
          raw: kelas,
          semesterData: getSemesterData(kelas),
        };
      })
      .filter((item) => item.kelasId)
      .sort((a, b) =>
        String(a.title).localeCompare(String(b.title), "id", {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [row, gradeLevelId, getSemesterData]);

  const getExistingStudentsFor = useCallback(
    (kelasRaw, kelasId, semesterNumber) => {
      const key = getStudentKey?.(row, kelasId, semesterNumber);

      if (key && Array.isArray(studentMap?.[key])) {
        return studentMap[key].filter(
          (student) => String(student?.state_form ?? "1") === "1"
        );
      }

      const semesterData = getSemesterDataByNumber(kelasRaw, semesterNumber) || {};
      const students =
        semesterData?.students ||
        semesterData?.student ||
        semesterData?.data_students ||
        [];

      return (Array.isArray(students) ? students : []).filter(
        (student) => String(student?.state_form ?? "1") === "1"
      );
    },
    [getStudentKey, getSemesterDataByNumber, row, studentMap]
  );

  const getExistingStudents = useCallback(
    (kelasItem) =>
      getExistingStudentsFor(
        kelasItem?.raw,
        kelasItem?.kelasId,
        sourceSemesterNumber
      ),
    [getExistingStudentsFor, sourceSemesterNumber]
  );

  const fetchStudents = useCallback(async () => {
    if (
      !show ||
      !row ||
      !form2?.ysb_branch_id ||
      !form2?.ysb_school_id ||
      !sourceSemesterId
    ) {
      setClassGroups([]);
      return;
    }

    if (availableClasses.length === 0) {
      setClassGroups([]);
      return;
    }

    setLoadingStudents(true);

    try {
      const responses = await Promise.all(
        availableClasses.map(async (kelas) => {
          try {
            const studentPayload = {
              ysb_semester_id: sourceSemesterId,
              ysb_school_id: form2.ysb_school_id,
              ysb_branch_id: form2.ysb_branch_id,
              ysb_kelas_id: kelas.kelasId,
            };

            const response = await APILA.get("/api/data-students", {
              ...fetchParams,

              // Browser tidak mengirim body GET secara konsisten.
              // Laravel tetap bisa membaca nilai ini sebagai request input
              // ketika dikirim melalui query params.
              params: studentPayload,

              // Tetap disertakan agar bentuk request masih sama dengan
              // dokumentasi/Postman apabila adapter yang dipakai mendukung GET body.
              data: studentPayload,
            });

            const responseData = response?.data?.data || {};
            const students = Array.isArray(responseData?.students)
              ? responseData.students
              : [];

            const normalizedStudents = students
              .map((student) => {
                const resolvedName = getStudentName(student);

                return {
                  ...student,
                  ysb_student_id: getStudentId(student),
                  // Samakan field nama dengan response student-config supaya
                  // modal daftar siswa langsung bisa menampilkan nama tanpa reload.
                  nama_lengkap: student?.nama_lengkap || resolvedName,
                  nama_siswa: student?.nama_siswa || resolvedName,
                  nisn: student?.nisn || student?.NISN || student?.nis || "",
                };
              })
              .filter((student) => student.ysb_student_id)
              .sort((a, b) =>
                String(getStudentName(a)).localeCompare(
                  String(getStudentName(b)),
                  "id",
                  {
                    numeric: true,
                    sensitivity: "base",
                  }
                )
              );

            return {
              ...kelas,
              title:
                responseData?.nama_kelas
                  ? `Kelas ${toTitleCase(
                      `${responseData?.nama_level || ""} ${responseData.nama_kelas}`.trim()
                    )}`
                  : kelas.title,
              waliUtama: responseData?.nama_walas_utama || "",
              waliPendamping: responseData?.nama_walas_pendamping || "",
              students: normalizedStudents,
              loadError: false,
            };
          } catch (error) {
            console.error(
              `Gagal mengambil siswa kelas ${kelas?.kelasId}`,
              error
            );

            return {
              ...kelas,
              students: [],
              loadError: true,
            };
          }
        })
      );

      setClassGroups(responses);

      const nextSelected = {};
      const nextExpanded = {};

      responses.forEach((kelas) => {
        const validStudentIds = new Set(
          kelas.students.map((student) => getStudentId(student))
        );

        const existingStudents = getExistingStudents(kelas);
        nextSelected[kelas.kelasId] = existingStudents
          .map((student) => getStudentId(student))
          .filter((id) => id && validStudentIds.has(id));

        // Saat modal pertama dibuka, semua kelas dibuat tertutup.
        nextExpanded[kelas.kelasId] = false;
      });

      setSelectedByClass(nextSelected);
      setExpandedClass(nextExpanded);
    } catch (error) {
      console.error("Gagal mengambil data siswa", error);
      setClassGroups([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message || "Gagal mengambil data siswa",
      });
    } finally {
      setLoadingStudents(false);
    }
  }, [
    show,
    row,
    form2?.ysb_branch_id,
    form2?.ysb_school_id,
    sourceSemesterId,
    sourceSemesterNumber,
    availableClasses,
    fetchParams,
    getExistingStudents,
  ]);

  const resetModalState = () => {
    setClassGroups([]);
    setSelectedByClass({});
    setExpandedClass({});
    setStudentSearch("");
    setLoadingStudents(false);
    setApplyingStudents(false);
    setSelectedSemesters([]);
  };

  const handleClose = () => {
    if (applyingStudents) return;
    resetModalState();
    onHide?.();
  };

  const handleEntered = () => {
    setStudentSearch("");
    setSelectedSemesters(
      targetSemesterOption?.number ? [targetSemesterOption.number] : []
    );
    fetchStudents();
  };

  const toggleSemester = (semesterNumber) => {
    if (applyingStudents) return;

    setSelectedSemesters((prev) => {
      const exists = prev.some(
        (item) => String(item) === String(semesterNumber)
      );

      if (exists) {
        return prev.filter(
          (item) => String(item) !== String(semesterNumber)
        );
      }

      return [...prev, semesterNumber].sort((a, b) => Number(a) - Number(b));
    });
  };

  const toggleExpanded = (kelasId) => {
    setExpandedClass((prev) => ({
      ...prev,
      [kelasId]: !prev[kelasId],
    }));
  };

  const toggleStudent = (kelasId, studentId) => {
    if (applyingStudents) return;

    setSelectedByClass((prev) => {
      const current = Array.isArray(prev[kelasId]) ? prev[kelasId] : [];
      const exists = current.includes(studentId);

      return {
        ...prev,
        [kelasId]: exists
          ? current.filter((id) => id !== studentId)
          : [...current, studentId],
      };
    });
  };

  const toggleWholeClass = (kelas) => {
    if (applyingStudents || kelas?.loadError) return;

    const allIds = kelas.students.map((student) => getStudentId(student));
    const current = selectedByClass[kelas.kelasId] || [];
    const isAllSelected =
      allIds.length > 0 && allIds.every((id) => current.includes(id));

    setSelectedByClass((prev) => ({
      ...prev,
      [kelas.kelasId]: isAllSelected ? [] : allIds,
    }));
  };

  const filteredGroups = useMemo(() => {
    const keyword = studentSearch.trim().toLowerCase();
    if (!keyword) return classGroups;

    return classGroups.map((kelas) => ({
      ...kelas,
      filteredStudents: kelas.students.filter((student) => {
        const haystack = [
          getStudentName(student),
          student?.nisn,
          student?.nis,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(keyword);
      }),
    }));
  }, [classGroups, studentSearch]);

  const selectedStudentCount = useMemo(
    () =>
      Object.values(selectedByClass).reduce(
        (total, ids) => total + (Array.isArray(ids) ? ids.length : 0),
        0
      ),
    [selectedByClass]
  );

  const selectedClassCount = useMemo(
    () =>
      Object.values(selectedByClass).filter(
        (ids) => Array.isArray(ids) && ids.length > 0
      ).length,
    [selectedByClass]
  );

  const selectedSemesterCount = selectedSemesters.length;

  const selectedSemesterLabels = useMemo(
    () =>
      selectedSemesters
        .map((semesterNumber) =>
          getSemesterChoiceByNumber(semesterNumber)?.label ||
          `Semester ${semesterNumber}`
        )
        .join(", "),
    [selectedSemesters, getSemesterChoiceByNumber]
  );

  const handleApplyStudents = async () => {
    if (!isUpdateMode && selectedStudentCount === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu siswa",
      });
      return;
    }

    if (selectedSemesterCount === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu semester",
      });
      return;
    }

    const matpelId = row?.ysb_matpel_id || row?.id || "";
    if (!matpelId) {
      ToastError.fire({
        icon: "error",
        title: "ID ekstrakurikuler tidak ditemukan",
      });
      return;
    }

    const selectedSemesterChoices = selectedSemesters
      .map((semesterNumber) => getSemesterChoiceByNumber(semesterNumber))
      .filter(Boolean);

    setApplyingStudents(true);

    try {
      const itemMap = new Map();
      const classSelections = [];

      const pushItem = (studentId, kelasId, semesterId) => {
        const normalizedStudentId = normalizeId(studentId);
        const normalizedKelasId = normalizeId(kelasId);
        const normalizedSemesterId = normalizeId(semesterId);

        if (
          !normalizedStudentId ||
          !normalizedKelasId ||
          !normalizedSemesterId
        ) {
          return;
        }

        const key = `${normalizedStudentId}__${normalizedKelasId}__${normalizedSemesterId}`;

        itemMap.set(key, {
          ysb_student_id: normalizedStudentId,
          ysb_kelas_id: normalizedKelasId,
          ysb_semester_id: normalizedSemesterId,
        });
      };

      if (isUpdateMode) {
        /*
         * Endpoint update-bulk bekerja sebagai sinkronisasi:
         * - siswa yang ADA di items = aktif / tercentang
         * - siswa yang TIDAK ADA di items = nonaktif / tidak tercentang
         *
         * Karena itu, saat mengubah satu semester kita tetap ikut mengirim
         * siswa aktif dari semester lain agar data yang tidak sedang diedit
         * tidak ikut menjadi nonaktif.
         */
        const targetLevel = (row?.grade_levels || []).find(
          (level) =>
            String(level?.ysb_grade_level_id) === String(gradeLevelId)
        );

        if (!targetLevel) {
          throw new Error("Data tingkatan ekstrakurikuler tidak ditemukan");
        }

        const selectedSemesterNumberSet = new Set(
          selectedSemesterChoices.map((item) => String(item.number))
        );

        const classGroupMap = new Map(
          classGroups.map((kelas) => [String(kelas.kelasId), kelas])
        );

        const allClasses = Array.isArray(targetLevel?.kelas)
          ? targetLevel.kelas
          : [];

        allClasses.forEach((kelasRaw) => {
          const kelasId = normalizeId(
            kelasRaw?.ysb_kelas_id ||
              kelasRaw?.kelas_id ||
              kelasRaw?.id ||
              ""
          );

          if (!kelasId) return;

          semesterChoices.forEach((semesterChoice) => {
            const semesterData = getSemesterDataByNumber(
              kelasRaw,
              semesterChoice.number
            );

            // Hanya proses kelas-semester yang memang aktif pada konfigurasi ekskul.
            if (String(semesterData?.state_form) !== "1") return;

            const isEditedSemester = selectedSemesterNumberSet.has(
              String(semesterChoice.number)
            );
            const classGroup = classGroupMap.get(String(kelasId));

            if (isEditedSemester && classGroup) {
              const selectedIds = Array.isArray(selectedByClass[kelasId])
                ? selectedByClass[kelasId]
                : [];
              const selectedSet = new Set(selectedIds.map((id) => String(id)));
              const selectedStudents = classGroup.students.filter((student) =>
                selectedSet.has(String(getStudentId(student)))
              );

              selectedIds.forEach((studentId) =>
                pushItem(studentId, kelasId, semesterChoice.id)
              );

              // Tetap simpan selection meskipun kosong. Ini penting agar
              // uncheck semua siswa langsung tercermin di UI.
              classSelections.push({
                kelasId,
                semesterNumber: semesterChoice.number,
                students: selectedStudents,
              });

              return;
            }

            // Semester/kelas yang tidak sedang diedit harus dipertahankan.
            const existingStudents = getExistingStudentsFor(
              kelasRaw,
              kelasId,
              semesterChoice.number
            );

            existingStudents.forEach((student) =>
              pushItem(getStudentId(student), kelasId, semesterChoice.id)
            );
          });
        });
      } else {
        // Mode tambah tetap memakai endpoint store seperti sebelumnya.
        classGroups.forEach((kelas) => {
          const selectedIds = selectedByClass[kelas.kelasId] || [];
          if (selectedIds.length === 0) return;

          const selectedSet = new Set(selectedIds.map((id) => String(id)));
          const selectedStudents = kelas.students.filter((student) =>
            selectedSet.has(String(getStudentId(student)))
          );

          selectedSemesterChoices.forEach((semesterChoice) => {
            const semesterData = getSemesterDataByNumber(
              kelas.raw,
              semesterChoice.number
            );

            if (String(semesterData?.state_form) !== "1") return;

            selectedIds.forEach((studentId) =>
              pushItem(studentId, kelas.kelasId, semesterChoice.id)
            );

            classSelections.push({
              kelasId: kelas.kelasId,
              semesterNumber: semesterChoice.number,
              students: selectedStudents,
            });
          });
        });
      }

      const items = Array.from(itemMap.values());

      if (!isUpdateMode && items.length === 0) {
        ToastError.fire({
          icon: "error",
          title: "Tidak ada kombinasi kelas dan semester yang bisa disimpan",
        });
        return;
      }

      const payload = {
        ysb_academic_year_id: form2?.ysb_semester_id,
        ysb_branch_id: form2?.ysb_branch_id,
        ysb_school_id: form2?.ysb_school_id,
        ysb_grade_level_id: gradeLevelId,
        ysb_matpel_id: matpelId,
        items,
        user_id: outputIdUser,
      };

      const response = isUpdateMode
        ? await APISD.put(
            "/api/ekstrakurikuler-students/update-bulk",
            payload,
            fetchParams
          )
        : await APISD.post(
            "/api/ekstrakurikuler-students/store",
            payload,
            fetchParams
          );

      setStudentMap?.((prev) => {
        const next = { ...prev };

        classSelections.forEach((selection) => {
          const key = getStudentKey?.(
            row,
            selection.kelasId,
            selection.semesterNumber
          );

          if (!key) return;

          const normalizedStudents = selection.students.map((student) => {
            const resolvedName = getStudentName(student);

            return {
              ...student,
              ysb_student_id: getStudentId(student),
              nama_lengkap: student?.nama_lengkap || resolvedName,
              nama_siswa: student?.nama_siswa || resolvedName,
              nisn: student?.nisn || student?.NISN || student?.nis || "",
              state_form: 1,
            };
          });

          if (isUpdateMode) {
            // Update harus mengganti isi lama, bukan merge, supaya siswa
            // yang di-uncheck benar-benar hilang dari state lokal.
            next[key] = normalizedStudents;
            return;
          }

          const existing = Array.isArray(next[key]) ? next[key] : [];
          const byId = new Map();

          [...existing, ...normalizedStudents].forEach((student) => {
            const studentId = getStudentId(student);
            if (!studentId) return;

            byId.set(studentId, {
              ...student,
              state_form: 1,
            });
          });

          next[key] = Array.from(byId.values());
        });

        return next;
      });

      ToastSuccess.fire({
        icon: "success",
        title:
          response?.data?.message ||
          (isUpdateMode
            ? `Data siswa berhasil diperbarui untuk ${selectedSemesterLabels}`
            : `Siswa berhasil disimpan ke ${selectedSemesterLabels}`),
      });

      // Tunggu refresh selesai supaya total siswa, daftar siswa, dan tombol
      // Tambah/Kelola langsung sinkron dengan response terbaru dari backend.
      await GetResponseData?.();

      resetModalState();
      onHide?.();
    } catch (error) {
      console.error(
        isUpdateMode
          ? "Gagal memperbarui siswa ekstrakurikuler"
          : "Gagal menyimpan siswa ekstrakurikuler",
        error
      );

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          (isUpdateMode
            ? "Gagal memperbarui siswa ekstrakurikuler"
            : "Gagal menyimpan siswa ekstrakurikuler"),
      });
    } finally {
      setApplyingStudents(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEntered={handleEntered}
      onExited={resetModalState}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="cm-student-modal-dialog"
    >
      <div className="cm-student-modal-shell">
        <div className="cm-student-modal-header">
          <div className="cm-student-modal-heading">
            <span className="cm-student-modal-heading-icon">
              <FontAwesomeIcon icon={faUsers} />
            </span>
            <div>
              <span className="cm-student-modal-eyebrow">
                {isUpdateMode ? "Kelola Siswa" : "Tambah Siswa"}
              </span>
              <h5>{row?.name_matpel || "Ekstrakurikuler"}</h5>
              <p>
                {isUpdateMode
                  ? "Centang siswa yang tetap aktif dan hilangkan centang untuk menonaktifkan, lalu simpan perubahan."
                  : "Pilih siswa dari kelas pada tingkatan yang sama, lalu tentukan semester tujuan."}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="cm-student-modal-close"
            onClick={handleClose}
            disabled={applyingStudents}
            aria-label="Tutup modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <Modal.Body className="cm-student-modal-body mt-1 mx-1">
          <div className="cm-student-semester-panel">
            <div className="cm-student-semester-copy">
              <strong>Pilih Semester</strong>
              <span>Bisa pilih Semester 1 dan Semester 2 sekaligus.</span>
            </div>

            <div className="cm-student-semester-options">
              {semesterChoices.map((semester) => {
                const checked = selectedSemesters.some(
                  (item) => String(item) === String(semester.number)
                );

                return (
                  <label
                    className={`cm-student-semester-check ${
                      checked ? "is-checked" : ""
                    }`}
                    key={`student-semester-${semester.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSemester(semester.number)}
                      disabled={applyingStudents}
                    />
                    <span>{semester.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="cm-student-toolbar">
            <div>
              <strong>{selectedStudentCount} siswa dipilih</strong>
              <span>{selectedClassCount} kelas memiliki pilihan</span>
            </div>

            <div className="cm-student-search-box">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                type="text"
                value={studentSearch}
                onChange={(event) => setStudentSearch(event.target.value)}
                placeholder="Cari nama siswa atau NISN..."
                disabled={loadingStudents || applyingStudents}
              />
              {studentSearch && (
                <button
                  type="button"
                  onClick={() => setStudentSearch("")}
                  disabled={applyingStudents}
                  aria-label="Hapus pencarian"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          </div>

          {loadingStudents ? (
            <div className="cm-student-loading">
              <PulseLoader color="#3f5bea" size={9} />
              <span>Memuat daftar siswa per kelas...</span>
            </div>
          ) : !sourceSemesterId ? (
            <div className="cm-student-empty-box">
              ID semester tidak ditemukan. Pastikan data semester sudah tersedia.
            </div>
          ) : classGroups.length === 0 ? (
            <div className="cm-student-empty-box">
              Tidak ada kelas untuk ekstrakurikuler ini pada Semester {" "}
              {sourceSemesterNumber}.
            </div>
          ) : (
            <div className="cm-student-class-list">
              {filteredGroups.map((kelas) => {
                const students = kelas.filteredStudents || kelas.students;
                const selectedIds = selectedByClass[kelas.kelasId] || [];
                const allStudentIds = kelas.students.map((student) =>
                  getStudentId(student)
                );
                const selectedInClass = selectedIds.length;
                const isAllSelected =
                  allStudentIds.length > 0 &&
                  allStudentIds.every((id) => selectedIds.includes(id));
                const isPartiallySelected =
                  selectedInClass > 0 && !isAllSelected;
                const isExpanded = !!expandedClass[kelas.kelasId];

                return (
                  <section
                    className="cm-student-class-card"
                    key={kelas.kelasId}
                  >
                    <div className="cm-student-class-header">
                      <button
                        type="button"
                        className={`cm-student-class-select ${
                          isAllSelected || isPartiallySelected
                            ? "is-selected"
                            : ""
                        }`}
                        onClick={() => toggleWholeClass(kelas)}
                        disabled={
                          applyingStudents ||
                          kelas.loadError ||
                          kelas.students.length === 0
                        }
                        title="Pilih seluruh siswa di kelas ini"
                      >
                        <span className="cm-student-check-box">
                          {isAllSelected ? (
                            <FontAwesomeIcon icon={faCheck} />
                          ) : isPartiallySelected ? (
                            <FontAwesomeIcon icon={faMinus} />
                          ) : null}
                        </span>

                        <span className="cm-student-class-icon">
                          <FontAwesomeIcon icon={faUserGroup} />
                        </span>

                        <span className="cm-student-class-copy">
                          <strong>{kelas.title}</strong>
                          <small>
                            {kelas.students.length} siswa · {selectedInClass} dipilih
                          </small>
                        </span>
                      </button>

                      <button
                        type="button"
                        className="cm-student-expand-button"
                        onClick={() => toggleExpanded(kelas.kelasId)}
                        aria-label={isExpanded ? "Tutup daftar siswa" : "Buka daftar siswa"}
                      >
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                        />
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="cm-student-class-body">
                        {kelas.loadError ? (
                          <div className="cm-student-empty-box is-small">
                            Gagal mengambil daftar siswa kelas ini.
                          </div>
                        ) : kelas.students.length === 0 ? (
                          <div className="cm-student-empty-box is-small">
                            Siswa pada kelas ini belum tersedia.
                          </div>
                        ) : students.length === 0 ? (
                          <div className="cm-student-empty-box is-small">
                            Siswa yang dicari tidak ditemukan di kelas ini.
                          </div>
                        ) : (
                          <div className="cm-student-grid">
                            {students.map((student) => {
                              const studentId = getStudentId(student);
                              const checked = selectedIds.includes(studentId);

                              return (
                                <button
                                  type="button"
                                  className={`cm-student-item ${
                                    checked ? "is-selected" : ""
                                  }`}
                                  key={studentId}
                                  onClick={() =>
                                    toggleStudent(kelas.kelasId, studentId)
                                  }
                                  disabled={applyingStudents}
                                >
                                  <span className="cm-student-item-check">
                                    {checked ? (
                                      <FontAwesomeIcon icon={faCheck} />
                                    ) : null}
                                  </span>

                                  <span className="cm-student-item-avatar">
                                    {getStudentInitials(student) || (
                                      <FontAwesomeIcon icon={faUser} />
                                    )}
                                  </span>

                                  <span className="cm-student-item-copy">
                                    <strong>
                                      {toTitleCase(getStudentName(student))}
                                    </strong>
                                    <small>
                                      NISN: {student?.nisn || "-"}
                                    </small>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </Modal.Body>

        <div className="cm-student-modal-footer">
          <div className="cm-student-footer-summary">
            <strong>{selectedStudentCount} siswa</strong> dari {" "}
            <strong>{selectedClassCount} kelas</strong> · {" "}
            <strong>{selectedSemesterCount} semester</strong> dipilih.
          </div>

          <div className="cm-student-footer-actions">
            <button
              type="button"
              className="cm-student-btn cm-student-btn-secondary"
              onClick={handleClose}
              disabled={applyingStudents}
            >
              Batal
            </button>

            <button
              type="button"
              className="cm-student-btn cm-student-btn-primary"
              onClick={handleApplyStudents}
              disabled={
                applyingStudents ||
                loadingStudents ||
                (!isUpdateMode && selectedStudentCount === 0) ||
                selectedSemesterCount === 0
              }
            >
              {applyingStudents ? (
                isUpdateMode ? "Memperbarui..." : "Menyimpan..."
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  {isUpdateMode ? "Simpan Perubahan" : "Simpan Siswa"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
